import React, { useEffect, useState } from "react";
import countryList from "react-select-country-list";
import { useAuth } from "../context/AuthContext";
import { getBlocklist, updateBlocklist } from "../api/authApi";
import Select from "react-select";

// Basic popular providers you might want to offer as quick block options
const BASIC_EMAIL_PROVIDERS = [
  { value: "gmail.com", label: "gmail.com" },
  { value: "yahoo.com", label: "yahoo.com" },
  { value: "hotmail.com", label: "hotmail.com" },
  { value: "outlook.com", label: "outlook.com" },
  { value: "protonmail.com", label: "protonmail.com" },
  { value: "aol.com", label: "aol.com" },
  { value: "icloud.com", label: "icloud.com" },
];

const ALL_COUNTRIES = countryList().getData();

export default function BlocklistsPanel() {
  const { user } = useAuth();
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [blockedProviders, setBlockedProviders] = useState([]);
  const [customProvider, setCustomProvider] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load blocklist from backend
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getBlocklist().then((res) => {
      console.log(res.data.countries)
      console.log(res.data.email_providers)
      setBlockedCountries(res.data.countries || []);
      setBlockedProviders(res.data.email_providers || []);
      setLoading(false);
    });
  }, [user]);

  // Toggle block/unblock country
  const handleToggleCountry = async (code) => {
    let newBlocked;
    if (blockedCountries.includes(code)) {
      newBlocked = blockedCountries.filter((c) => c !== code);
    } else {
      newBlocked = [...blockedCountries, code];
    }
    setBlockedCountries(newBlocked);
    setSaving(true);
    await updateBlocklist({ countries: newBlocked, email_providers: blockedProviders });
    setSaving(false);
  };

  // Update email provider blocklist
  const handleProviderChange = async (selectedOptions) => {
    const newProviders = selectedOptions.map((opt) => opt.value);
    setBlockedProviders(newProviders);
    setSaving(true);
    await updateBlocklist({ countries: blockedCountries, email_providers: newProviders });
    setSaving(false);
  };

  // Add custom provider
  const handleAddCustomProvider = async (e) => {
    e.preventDefault();
    if (!customProvider.trim() || blockedProviders.includes(customProvider.trim().toLowerCase())) return;
    const newProviders = [...blockedProviders, customProvider.trim().toLowerCase()];
    setBlockedProviders(newProviders);
    setCustomProvider("");
    setSaving(true);
    await updateBlocklist({ countries: blockedCountries, email_providers: newProviders });
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        Loading blocklist…
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Blocklists</h2>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Blocked Email Providers</h3>
        <p className="mb-2 text-gray-600">
          Select email providers to block (users with emails from these will be blocked at checkout).
        </p>
        <Select
          isMulti
          options={BASIC_EMAIL_PROVIDERS}
          value={BASIC_EMAIL_PROVIDERS.filter(opt => blockedProviders.includes(opt.value))}
          onChange={handleProviderChange}
          className="mb-2"
          placeholder="Choose basic providers..."
        />
        <form onSubmit={handleAddCustomProvider} className="flex gap-2 mt-1">
          <input
            type="text"
            className="form-input flex-1"
            placeholder="Add custom provider (e.g. mailinator.com)"
            value={customProvider}
            onChange={e => setCustomProvider(e.target.value)}
            disabled={saving}
          />
          <button
            className="bg-blue-600 text-white px-3 py-1 rounded"
            type="submit"
            disabled={!customProvider.trim() || saving}
          >
            Add
          </button>
        </form>
        <div className="mt-2">
          {blockedProviders.length > 0 && (
            <div>
              <span className="font-medium">Blocked:</span>{" "}
              {blockedProviders.join(", ")}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Country Blocklist</h3>
        <p className="mb-2 text-gray-600">
          Toggle the countries you wish to block for payments.<br />
          (ISO-2 codes: e.g. US, FR, NG)
        </p>
        <div className="overflow-auto max-h-[40vh] border p-2 rounded-lg bg-gray-50">
          <ul className="space-y-2">
            {ALL_COUNTRIES.map((c) => (
              <li
                key={c.value}
                className="flex items-center justify-between px-3 py-2 bg-white rounded border"
              >
                <span className="text-gray-900">
                  {c.label} <span className="text-xs text-gray-500">({c.value})</span>
                </span>
                <input
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-blue-600"
                  checked={blockedCountries.includes(c.value)}
                  onChange={() => handleToggleCountry(c.value)}
                  disabled={saving}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-4 text-sm text-gray-500">
        <b>Tip:</b> You can add any custom email provider domain, and block/unblock countries at any time.
      </div>
    </div>
  );
}
